<template>
    <v-card-text class="px-0">
        <div>
            <p class="title primary--text mt-0 font-weight-bold">
                Send to
            </p>
        </div>

        <v-form ref="form">
            <v-expand-transition>
                <div v-if="recipients.length || loading">
                    <v-autocomplete
                        v-model="selectedRecipient"
                        name="recipient"
                        label="Select Recipient"
                        :items="recipients"
                        item-text="name"
                        item-value="name"
                        item-color="primary"
                        color="primary"
                        clearable
                        return-object
                    />
                </div>
            </v-expand-transition>

            <v-expand-transition>
                <v-text-field
                    v-model="selectedRecipient.name"
                    name="name"
                    v-validate="'required|name|two-words|max:255'"
                    aria-required
                    required
                    label="Recipient Name"
                    color="primary"
                    validate-on-blur
                    :error-messages="errors.first('name')"
                >
                </v-text-field>
            </v-expand-transition>

            <v-text-field
                key="email"
                v-model="selectedRecipient.email"
                v-validate="'required|email'"
                validate-on-blur
                aria-required
                required
                name="email"
                label="Email"
                color="primary"
                :error-messages="errors.first('email')"
                hint="Enter the recipient's email address if you want them to receive an email when the money is delivered."
                persistent-hint
            />

            <v-text-field
                v-model="selectedRecipient.phone"
                v-validate="'phone-plus|phone-length'"
                type="text"
                class="mt-5 mb-n5"
                label="Phone"
                name="phone"
                color="primary"
                hint="Enter phone number starting with country code e.g. +1, +234, +44, etc."
                :error-messages="errors.first('phone')"
                @focus="$event.target.select()"
            />

            <v-autocomplete
                class="mt-5"
                ref="country"
                v-model="selectedRecipient.country"
                v-validate="'required'"
                aria-required
                required
                :error-messages="errors.first('country')"
                name="country"
                label="Recipient Country"
                :items="countries"
                item-text="name"
                item-value="name"
                item-color="primary"
                color="primary"
                background-color="#f8f8fd"
            />

            <v-expand-transition>
                <div v-if="deliveryMethods">
                    <v-autocomplete
                        v-model="selectedRecipient.paymentType"
                        v-validate="'required'"
                        :error-messages="errors.first('delivery method')"
                        aria-required
                        required
                        name="delivery method"
                        label="Delivery Method"
                        :items="deliveryMethods"
                        item-text="method"
                        item-color="primary"
                        color="primary"
                    />
                </div>
            </v-expand-transition>

            <v-expand-transition v-if="banks.length">
                <v-autocomplete
                    v-model="selectedRecipient.bank"
                    v-validate="'required'"
                    :error-messages="errors.first('bank')"
                    aria-required
                    required
                    name="bank"
                    label="Bank"
                    :items="banks"
                    item-text="Name"
                    return-object
                    item-color="primary"
                    color="primary"
                />
            </v-expand-transition>

            <v-expand-transition>
                <div v-if="paymentTypeIsBank">
                    <v-text-field
                        v-model="selectedRecipient.account_number"
                        v-validate="'required|numeric'"
                        name="account number"
                        ref="account_number"
                        aria-required
                        required
                        label="Recipient Account number"
                        color="primary"
                        validate-on-blur
                        :error-messages="errors.first('account number')"
                    >
                        <template #append>
                            <v-fade-transition>
                                <v-icon
                                    v-if="loading"
                                    color="info"
                                    v-text="`mdi-loading mdi-spin`"
                                />
                            </v-fade-transition>
                        </template>
                    </v-text-field>
                </div>
            </v-expand-transition>

            <div>
                <v-switch
                    v-model="selectedRecipient.save_details"
                    color="primary"
                    class="my-0"
                    label="Save Recipient Details"
                ></v-switch>
            </div>

            <div class="my-4">
                <v-btn
                    class="white--text title pa-6 text-none rounded-lg"
                    color="primary"
                    block
                    @click="proceed"
                >
                    Proceed
                </v-btn>
            </div>
        </v-form>
    </v-card-text>
</template>

<script>
import recipients from '../modules/recipients.json';
import countries from '../../../modules/countries.json';
import FlutterwaveService from '../../../services/FlutterwaveService';

export default {
    data() {
        return {
            recipients,
            deliveryMethods: [
                {
                    value: 'bank-transfer',
                    method: 'Bank Transfer',
                }
            ],
            banks: [],
            selectedRecipient: {
                save_details: false,
                paymentType: null,
                bank: null,
                account_number: null,
            },
            countries,
        }
    },
    props: {
        recipient: Object,
        loading: Boolean,
    },
    computed: {
        paymentTypeIsBank() {
            const { selectedRecipient } = this;
            return selectedRecipient.paymentType === 'bank-transfer';
        },
    },
    created() {
        this.selectedRecipient = {
            bank: null,
            account_number: null,
            paymentType: null,
            save_details: false,
            ...this.recipient,
        }
    },
    methods: {
        proceed() {
            const vm = this;
            const{ selectedRecipient } = this;
            vm.$validator.validateAll().then(submitIfValid);

            function submitIfValid(formIsValid) {
                if(formIsValid){
                    vm.$emit('proceed', selectedRecipient)
                }
            }
        },
        async loadBanks(countryAbbrev) {
            this.$emit('update:loading', true);

            if (!countryAbbrev) {
                this.$swal({
                    type: 'error',
                    title: 'Poster country is empty',
                });
            }

            try {
                const banks = await FlutterwaveService.getBanksByCountry(countryAbbrev);
                if (banks.length === 0) {
                    this.$swal({
                        type: 'error',
                        title: 'No banks found',
                    });
                } else {
                    this.banks = banks;
                }
            } catch (err) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading banks',
                    text: err,
                })
            } finally {
                this.$emit('update:loading', false);
            }
        }
    },
    watch: {
        'selectedRecipient.country'() {
            const { country } = this.selectedRecipient;
            console.log({ country })
            const countryObj = countries.find(key => key.name.toLowerCase().includes(country.toLowerCase()))
            if(countryObj) {
                this.loadBanks(countryObj.iso2);
            }
        }
    }
}
</script>
